import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { i18n } from "../../../translate/i18n";
import {
  replySimulation,
  startSimulation,
  stopSimulation,
} from "../../../services/flowSimulator.api";

/**
 * Estado de una sesión de previsualización (0383 FS-2.2).
 *
 * El trace llega completo del backend en cada llamada; aquí solo se reproduce paso a paso para
 * que el usuario vea el recorrido en cascada, igual que en Kommo. Por eso no hace falta
 * Socket.IO: la animación es de presentación, no de transporte.
 */

const CASCADE_MS = 450;

const useFlowSimulator = ({ flowId, provider, getFlowSnapshot }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [contact, setContact] = useState({ name: "", number: "" });
  const [options, setOptions] = useState({ executeHttp: false, executeAi: false });
  const [whatsappId, setWhatsappId] = useState(null);

  const sessionIdRef = useRef(null);
  const isMountedRef = useRef(true);
  const cascadeTimerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (cascadeTimerRef.current) clearInterval(cascadeTimerRef.current);
      // Cierre garantizado: si el usuario navega fuera del editor, el sandbox no puede
      // quedarse vivo en la base del cliente.
      if (sessionIdRef.current) {
        stopSimulation(sessionIdRef.current).catch(() => {});
        sessionIdRef.current = null;
      }
    };
  }, []);

  /** Revela los pasos nuevos de uno en uno. */
  const playCascade = useCallback((fromIndex, total) => {
    if (cascadeTimerRef.current) clearInterval(cascadeTimerRef.current);
    let current = fromIndex;
    setVisibleCount(current);
    if (current >= total) return;

    cascadeTimerRef.current = setInterval(() => {
      current += 1;
      if (!isMountedRef.current || current > total) {
        clearInterval(cascadeTimerRef.current);
        cascadeTimerRef.current = null;
        return;
      }
      setVisibleCount(current);
    }, CASCADE_MS);
  }, []);

  const applyState = useCallback(
    (state, previousStepCount = 0) => {
      if (!isMountedRef.current) return;
      setSession(state);
      sessionIdRef.current = state.sessionId;
      playCascade(previousStepCount, state.steps.length);
    },
    [playCascade]
  );

  const handleError = useCallback((error) => {
    const backendMsg = error?.response?.data?.error || error?.response?.data?.message;
    toast.error(
      backendMsg ||
        i18n.t("flowbuilders.simulator.errors.generic") ||
        "No se pudo ejecutar la previsualización"
    );
  }, []);

  const start = useCallback(async () => {
    setLoading(true);
    try {
      // Se manda el grafo del editor: así se previsualiza lo que hay en pantalla, incluidos
      // los cambios sin guardar (el backend lo ejecuta sobre un flujo espejo).
      const { data } = await startSimulation({
        flowId,
        flow: getFlowSnapshot(),
        whatsappId: whatsappId || undefined,
        contact: {
          name: contact.name?.trim() || undefined,
          number: contact.number?.trim() || undefined,
        },
        options,
      });
      applyState(data, 0);
      setOpen(true);
    } catch (error) {
      handleError(error);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [flowId, getFlowSnapshot, whatsappId, contact, options, applyState, handleError]);

  const reply = useCallback(
    async (payload) => {
      if (!sessionIdRef.current) return;
      setLoading(true);
      const previous = session?.steps?.length || 0;
      try {
        const { data } = await replySimulation(sessionIdRef.current, payload);
        applyState(data, previous);
      } catch (error) {
        handleError(error);
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    },
    [session, applyState, handleError]
  );

  const stop = useCallback(async () => {
    const id = sessionIdRef.current;
    sessionIdRef.current = null;
    if (cascadeTimerRef.current) clearInterval(cascadeTimerRef.current);
    setOpen(false);
    setSession(null);
    setVisibleCount(0);
    if (id) {
      try {
        await stopSimulation(id);
      } catch {
        // El cron de purga del backend recoge el sandbox si este DELETE no llega.
      }
    }
  }, []);

  const restart = useCallback(async () => {
    await stop();
    await start();
  }, [stop, start]);

  const visibleSteps = (session?.steps || []).slice(0, visibleCount);
  const isPlaying = session ? visibleCount < session.steps.length : false;
  // Durante la cascada el nodo activo es el del último paso revelado; al terminar, el nodo
  // donde el motor dejó el flujo.
  const activeNodeId = isPlaying
    ? visibleSteps[visibleSteps.length - 1]?.nodeId || null
    : session?.currentNodeId || null;

  return {
    open,
    loading,
    session,
    visibleSteps,
    isPlaying,
    activeNodeId,
    visitedNodeIds: [...new Set((session?.steps || []).map((s) => s.nodeId))],
    contact,
    setContact,
    options,
    setOptions,
    whatsappId,
    setWhatsappId,
    start,
    reply,
    stop,
    restart,
  };
};

export default useFlowSimulator;
